import type { TreeSelectSource } from "app/pages/sales-intelligence/common-components/dropdown/TreeSelectCombobox/types";
import {
    collectAncestorsOfChecked,
    flattenVisibleRows,
    toggleNode,
} from "app/pages/sales-intelligence/utils/taxonomy-tree/tree";
import type { TaxonomyIndex } from "app/pages/sales-intelligence/utils/taxonomy-tree/types";

import type { TaxonomySearch } from "./search";

type FactoryDeps<TId extends string> = {
    readonly taxonomy: TaxonomyIndex<TId>;
    readonly value: readonly TId[];
    /** Held outside the factory so it survives a selection change; see `createSubstringTaxonomySearch`. */
    readonly search: TaxonomySearch<TId>;
};

/**
 * The multi-select reading of a signal taxonomy, generic over the id brand so intent and technology
 * share one implementation.
 *
 * `createIndustryTreeViewModel` holds an identical body wrapped in chip handling. Collapsing both onto
 * a shared `createMultiSelectTreeSource` beside `TreeSelectCombobox` is deferred: it means refactoring
 * the shipped industry filter, and the two search states cache on different keys (industry on the
 * trimmed query, this one on the normalised query), which has to be reconciled first.
 */
export const createSignalTreeSource = <TId extends string>({
    taxonomy,
    value,
    search,
}: FactoryDeps<TId>): TreeSelectSource<TId> => {
    const checked = new Set(value);
    const ancestorsOfChecked = collectAncestorsOfChecked(value, taxonomy);

    return {
        visibleNodeIds: search.visibleNodeIds,

        rows: (expanded, visible) => flattenVisibleRows(taxonomy, { expanded, checked, ancestorsOfChecked, visible }),

        toggleChecked: (id) => toggleNode(value, id, taxonomy),
    };
};
